import { atom, selectorFamily } from 'recoil'

import { BasePaginateMapper } from 'src/data/common/BaseMapper'
import { authenticatedCustomerState } from 'src/data/customers/CustomersAtoms'

import { sessionStorageEffect } from '../common/commonAtoms'

import { OrderMapper } from './OrderMapper'
import { OrdersService } from './OrdersService'
import {
  FulfillmentModel,
  OrderModel,
  PaginateOrdersModel,
} from './type'

export const accountPaginatedOrdersStateFamily = atom<PaginateOrdersModel>({
  key: 'account_orders',
  default: BasePaginateMapper.defaultValue,
})

export const accountPaginatedOrdersSelectorFamily = selectorFamily<
  PaginateOrdersModel,
  {customerId: number, isChild: boolean}
>({
  key: 'account_orders_selector',
  get:
    ({customerId, isChild}) =>
    ({ get }) => {
      const orders = get(accountPaginatedOrdersStateFamily)
      if (orders.count === 0 && orders.page_count === 0) {
        const promise = OrdersService.getOrdersPaginated(customerId, 1, isChild)
        return promise
      }
      return orders
    },

  set:
    () =>
    ({ set }, currentPagination) => {
      set(accountPaginatedOrdersStateFamily, currentPagination)
    },
})

export const orderFulfillmentsSelector = selectorFamily<
  FulfillmentModel[],
  {orderId: string | undefined, isChild: boolean}
>({
  key: 'order_fulfillments',
  get: ({orderId, isChild}) => () =>
    orderId ? OrdersService.getOrderFulfillments(parseInt(orderId), isChild) : [],
})

export const orderDetailsSelector = selectorFamily<
  OrderModel,
  { orderId: string | undefined,
    isChild?: boolean,
  }
>({
  key: 'order',
  get: ({orderId, isChild}) => () =>
    orderId ? OrdersService.getOrder(orderId, isChild ?? false) : OrderMapper.defaultValue,
})

export const orderReceiptSelector = selectorFamily<string, { orderId: number, currency: string, isChild?: boolean }>({
  key: 'order_receipt',
  get:
    ({ orderId, currency, isChild }) =>
      async ({ get }) => {
        try {
          const customer = get(authenticatedCustomerState)
          let result = await OrdersService.getOrderReceipt(
            orderId,
            customer.language,
            currency,
            isChild
          )
          return result
        } catch (error) {
          console.error(error)
          return ""
        }
      },
})

export const validateOrderReceiptSelector = selectorFamily<boolean, { orderId: number, currency: string, isChild?: boolean }>({
  key: 'order_validate_receipt',
  get:
    ({ orderId, currency, isChild }) =>
      async ({ get }) => {
        try {
          const customer = get(authenticatedCustomerState)
          const res = await OrdersService.validateOrderReceipt(
            orderId,
            customer.language,
            currency,
            isChild
          )
          return res.is_valid
        } catch (error) {
          console.error(error)
          return false
        }
      },
})

export const clientSecret = atom<string>({
  key: 'client_secret',
  default: '',
})

export const sezzleOrderUuidAtom = atom<string>({
  key: 'sezzle_order_uuid',
  default: '',
  effects: [sessionStorageEffect('sezzle_order_uuid')],
})
